/**
 * safeResolve — bounds-checked wrappers for ProseMirror `Node.resolve`.
 *
 * `doc.resolve(pos)` throws `RangeError: Position X out of range` when pos
 * exceeds `doc.content.size`. This can happen in the Yjs collaboration layer
 * when a remote update shrinks the document while a local transaction still
 * holds a reference to an old position. Sentry recorded 60 occurrences of
 * this error from 4 users (RangeError: Position X out of range).
 *
 * Usage:
 *   import { safeResolve } from '../utils/safeResolve';
 *   const $pos = safeResolve(state.doc, pos);
 *   if (!$pos) return; // doc shrank; skip this operation
 */

import * as Sentry from '@sentry/react';
import type { Node, ResolvedPos } from 'prosemirror-model';

/**
 * Attempt to resolve `pos` in `doc`. Returns `null` (instead of throwing) if
 * the position is out of range, and logs to Sentry so we can track frequency.
 */
export function safeResolve(doc: Node, pos: number): ResolvedPos | null {
  const size = doc.content.size;
  if (pos < 0 || pos > size) {
    Sentry.withScope((scope) => {
      scope.setTag('pm_doc_size', size);
      scope.setTag('pm_pos', pos);
      scope.setLevel('warning');
      Sentry.captureMessage(
        `[safeResolve] Position ${pos} out of range (doc size ${size}) — skipped`,
      );
    });
    return null;
  }
  try {
    return doc.resolve(pos);
  } catch (err) {
    Sentry.withScope((scope) => {
      scope.setTag('pm_doc_size', size);
      scope.setTag('pm_pos', pos);
      Sentry.captureException(err);
    });
    return null;
  }
}

/**
 * Clamp `pos` to the valid range `[0, doc.content.size]` and resolve it.
 * Use when you want to resolve the nearest valid position rather than skip.
 */
export function clampResolve(doc: Node, pos: number): ResolvedPos {
  const clamped = Math.max(0, Math.min(pos, doc.content.size));
  return doc.resolve(clamped);
}
